import { Client, Databases, Functions, ID, Query } from "appwrite";

// In your existing appwrite.js file, update this:
export const appwriteConfig = {
  endpoint: "https://cloud.appwrite.io/v1",
  projectId: "67e12daa0002eec157ab",
  databaseId: "67e2a157000d4c58cc93",
  messagerstyleId: "680b33410014634ed9d8",
  userCollectionId: "67e535d7003e35a9938d",
  conversationCollectionId: "67e555c10039e259d079", // ✅ FIXED
  messageCollectionId: "67e59d260035707e53e8", // ✅ FIXED
  storageId: "683afbae0017bdfae575",
  customerCollectionId: "67f92ae10033c01a8f8c",
  companyCollectionId: "67e535c100387bd4b7e8",
  aiDatabaseId: "68377fd80039fe8db0d5", // Your AI database ID
  aiAnalyticsCollectionId: "683a2585001004bb3265", // Your AI analytics collection ID
  visitorPresenceCollectionId: "683401ee001d674a5f7f",
};

const client = new Client();
client
  .setEndpoint(appwriteConfig.endpoint)
  .setProject(appwriteConfig.projectId);

const databases = new Databases(client);
const functions = new Functions(client);

// Cache for fetched data to avoid repeated API calls
let cachedUserData = null;
let cachedMessagerStyle = null;
let currentCompanyId = null;

// Function to set company ID (called from your widget)
export function setCompanyId(companyId) {
  // Only clear cache if company ID changes
  if (currentCompanyId !== companyId) {
    currentCompanyId = companyId;
    cachedUserData = null;
    cachedMessagerStyle = null;
  }
}

// Function to fetch user data from userCollection based on companyId
async function fetchUserData(companyId) {
  if (!companyId) {
    throw new Error("Company ID is required to fetch user data");
  }

  // Return cached data if available
  if (cachedUserData && currentCompanyId === companyId) {
    return cachedUserData;
  }

  try {
    const response = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.userCollectionId,
      [Query.equal("companyId", companyId)]
    );

    if (response.documents.length === 0) {
      throw new Error(`No user found for company ID: ${companyId}`);
    }

    // Take the first user found for this company
    cachedUserData = response.documents[0];

    return cachedUserData;
  } catch (error) {
    console.error("Error fetching user data:", error);
    throw error;
  }
}

// Function to fetch messager style data from messagerStyle collection
async function fetchMessagerStyle(companyId) {
  if (!companyId) {
    throw new Error("Company ID is required to fetch messager style");
  }

  // Return cached data if available
  if (cachedMessagerStyle && currentCompanyId === companyId) {
    return cachedMessagerStyle;
  }

  try {
    const response = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.messagerstyleId,
      [Query.equal("companyID", companyId)]
    );

    if (response.documents.length === 0) {
      throw new Error(`No messager style found for company ID: ${companyId}`);
    }

    cachedMessagerStyle = response.documents[0];

    return cachedMessagerStyle;
  } catch (error) {
    console.error("Error fetching messager style:", error);
    throw error;
  }
}

// Dynamic function to get current user based on fetched data
export async function getCurrentUser() {
  if (!currentCompanyId) {
    console.warn("No company ID set, using fallback user data");
    return {
      email: "support@chatly.com",
      uniqueEmail: "support@chatly.com",
      username: "Support Team",
      name: "Support Team",
      companyId: null,
      companyName: "Chatly Support",
    };
  }

  try {
    // Fetch both user data and messager style in parallel
    const [userData, messagerStyle] = await Promise.all([
      fetchUserData(currentCompanyId),
      fetchMessagerStyle(currentCompanyId),
    ]);

    // Combine data from both collections
    const dynamicUser = {
      // Core user info from userCollection
      accountId: userData.accountId,
      email: userData.email,
      uniqueEmail: userData.uniqueEmail || userData.email,
      username: userData.username,
      avatar: userData.avatar,

      // Use companyName from userCollection, fallback to messagerStyle
      name:
        userData.companyName || messagerStyle.companyName || userData.username,
      companyName: userData.companyName || messagerStyle.companyName,

      // Company and email settings
      companyId: currentCompanyId,
      outgoingEmail:
        userData.outgoingEmail || userData.uniqueEmail || userData.email,
      outgoingText:
        userData.outgoingText || userData.companyName || userData.username,

      // Email from messagerStyle (for contact purposes)
      supportEmail: messagerStyle.emailAddress,

      // Additional user data that might be useful
      phone: userData.phone,
      jobTitle: userData.jobTitle,
      department: userData.department,
      timezone: userData.timezone,
      workHoursStart: userData.workHoursStart,
      workHoursEnd: userData.workHoursEnd,
    };

    return dynamicUser;
  } catch (error) {
    console.error("Error getting current user:", error);

    // Fallback to basic data if fetching fails
    return {
      email: `support-${currentCompanyId}@chatly.com`,
      uniqueEmail: `support-${currentCompanyId}@chatly.com`,
      username: "Support Team",
      name: "Support Team",
      companyId: currentCompanyId,
      companyName: "Support Team",
    };
  }
}

// Send Email with dynamic company data
export async function sendEmail(emailData) {
  try {
    const currentUser = await getCurrentUser();

    // Get custom sender email and name or use dynamic defaults
    const fromEmail =
      emailData.fromEmail ||
      currentUser.outgoingEmail ||
      currentUser.uniqueEmail;
    const fromName =
      emailData.fromName || currentUser.outgoingText || currentUser.name;

    // Prepare email parameters with dynamic values
    const emailParams = {
      from: currentUser.uniqueEmail || currentUser.email, // System email (required for replies)
      outgoingEmail: fromEmail, // Custom sender email (for display in email clients)
      outgoingText: fromName, // Custom sender name (for display)
      userName: currentUser.name,
      to: emailData.to,
      subject: emailData.subject || `Message from ${currentUser.name}`,
      text: emailData.text,
    };

    // Call the email function
    const execution = await functions.createExecution(
      "680e33ff0004f96fe928", // Email function ID
      JSON.stringify(emailParams),
      false
    );

    return execution;
  } catch (error) {
    console.error("Error sending email:", error);
    throw error;
  }
}

// ✅ FIXED: Updated sendMessage function in appwrite.js
const sendMessage = async (
  conversationId,
  senderId,
  content,
  senderType = "customer",
  imageData = null
) => {
  try {
    // Ensure conversationId is a string
    const conversationIdString =
      typeof conversationId === "object" ? conversationId.$id : conversationId;

    const messageData = {
      conversation_id: conversationIdString,
      sender_id: senderId,
      sender_type: senderType,
      content: content || (imageData ? "📷 Image" : ""), // ✅ AI message goes HERE
      created_at: new Date().toISOString(),
      status: "sent",
      is_read: false,
      channel: "messenger", // ✅ FIXED: Channel should be short identifier, not message content
    };

    // ✨ Add image data if present
    if (imageData) {
      messageData.message_type = "image";
      messageData.image_url = imageData.fileUrl;
      messageData.image_file_id = imageData.fileId;
      messageData.file_name = imageData.originalName;
      messageData.file_size = imageData.fileSize;
      messageData.mime_type = imageData.mimeType;
      messageData.download_url = imageData.downloadUrl;
    } else {
      messageData.message_type = "text";
    }

    // Create the message document
    const message = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.messageCollectionId,
      ID.unique(),
      messageData
    );

    // Update conversation with preview
    const previewText = imageData ? "📷 Image" : content;
    const truncatedPreview =
      previewText.length > 100
        ? previewText.substring(0, 97) + "..."
        : previewText;

    await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.conversationCollectionId,
      conversationIdString,
      {
        last_message_preview: truncatedPreview,
        updated_at: new Date().toISOString(),
        unreadCount: 1,
        isNewConversation: false,
        lastReadBy: [],
        last_sender_id: senderId,
      }
    );

    return message;
  } catch (error) {
    console.error("❌ Error sending message:", error);
    throw error;
  }
};

// Create a new conversation with dynamic company data
export const createConversation = async (
  customerData,
  initialMessage,
  channel = "callback"
) => {
  try {
    const currentUser = await getCurrentUser();

    if (!currentUser.companyId) {
      throw new Error("No company ID available - cannot create conversation");
    }

    // Create conversation data with dynamic values
    const conversationData = {
      title: `${
        channel === "callback" ? "Callback Request" : "New Conversation"
      }: ${customerData.name}`,
      companyId: currentUser.companyId,
      channel: channel,
      status: "new",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      last_message_preview: initialMessage,
      participants: [currentUser.accountId || `agent-${currentUser.companyId}`],
    };

    // Create a new conversation
    const conversation = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.conversationCollectionId,
      ID.unique(),
      conversationData
    );

    // Add the initial message with customer details
    await sendMessage(conversation.$id, initialMessage, channel);

    return conversation;
  } catch (error) {
    console.error("Error creating conversation:", error);
    throw error;
  }
};

// Send callback emails with dynamic data
export async function sendCallbackEmails(callbackData) {
  try {
    const currentUser = await getCurrentUser();
    const messagerStyle = await fetchMessagerStyle(currentCompanyId);

    // Format the preferred time
    let readableTime = "Vormittags (9-12 Uhr)";
    if (callbackData.preferredTime === "afternoon")
      readableTime = "Nachmittags (12-17 Uhr)";
    if (callbackData.preferredTime === "evening")
      readableTime = "Abends (17-20 Uhr)";

    // Use support email from messagerStyle or fallback to user email
    const businessEmail =
      messagerStyle.emailAddress ||
      currentUser.supportEmail ||
      currentUser.email;

    // 1. Send confirmation email to customer if email provided
    if (callbackData.email) {
      const customerEmailContent = `
Hallo ${callbackData.name},

vielen Dank für deine Rückrufanfrage. Wir werden dich so bald wie möglich unter der Nummer ${callbackData.phone} kontaktieren.

Deine bevorzugte Zeit: ${readableTime}

Mit freundlichen Grüßen,
${currentUser.name}
      `.trim();

      await sendEmail({
        to: callbackData.email,
        subject: "Bestätigung deiner Rückrufanfrage",
        text: customerEmailContent,
        fromEmail: currentUser.outgoingEmail,
        fromName: currentUser.outgoingText || currentUser.name,
      });
    }

    // 2. Send notification email to business
    const businessEmailContent = `
Neue Rückrufanfrage:

Name: ${callbackData.name}
Telefon: ${callbackData.phone}
E-Mail: ${callbackData.email || "Nicht angegeben"}
Bevorzugte Zeit: ${readableTime}
    `.trim();

    await sendEmail({
      to: businessEmail,
      subject: "Neue Rückrufanfrage",
      text: businessEmailContent,
      fromEmail: currentUser.outgoingEmail,
      fromName: `${currentUser.name} - Benachrichtigungen`,
    });

    return {
      success: true,
      message: "Callback emails sent successfully",
    };
  } catch (error) {
    console.error("Error sending callback emails:", error);
    throw error;
  }
}

// Combined function for callback requests with emails
export async function processCallbackRequest(callbackData) {
  try {
    // Format the preferred time
    let readableTime = "Vormittags (9-12 Uhr)";
    if (callbackData.preferredTime === "afternoon")
      readableTime = "Nachmittags (12-17 Uhr)";
    if (callbackData.preferredTime === "evening")
      readableTime = "Abends (17-20 Uhr)";

    // Try to send emails first
    try {
      await sendCallbackEmails(callbackData);
    } catch (emailError) {
      console.error("Error sending emails:", emailError);
    }

    // Then create a conversation
    let conversation = null;
    try {
      const initialMessage = `
Rückrufanfrage von: ${callbackData.name}
Telefon: ${callbackData.phone}
E-Mail: ${callbackData.email || "Nicht angegeben"}
Bevorzugte Zeit: ${readableTime}
      `.trim();

      conversation = await createConversation(
        {
          name: callbackData.name,
          phone: callbackData.phone,
          email: callbackData.email,
        },
        initialMessage
      );
    } catch (convError) {
      console.error("Error creating conversation:", convError);
    }

    return {
      success: true,
      conversation: conversation,
      message: "Callback request processed",
    };
  } catch (error) {
    console.error("Error processing callback request:", error);
    throw error;
  }
}

// Visitor tracking with dynamic company ID
// In your appwrite.js file, update the trackVisitorPresence function:

export const trackVisitorPresence = async (visitorData) => {
  try {
    const presenceData = {
      visitorId: visitorData.visitorId,
      companyId: currentCompanyId || visitorData.companyId,
      websiteUrl: visitorData.websiteUrl,
      pageUrl: visitorData.pageUrl,
      isActive: true,
      lastSeen: new Date().toISOString(),
      country: visitorData.country,
      city: visitorData.city,
      device: visitorData.device,
      browser: visitorData.browser,
      widgetOpened: visitorData.widgetOpened || false,
      sessionStart: visitorData.sessionStart,
      referrer: visitorData.referrer,
      ipAddress: visitorData.ipAddress,
    };

    // ✅ FIXED: Check if collection ID exists
    if (!appwriteConfig.visitorPresenceCollectionId) {
      console.warn(
        "⚠️ Visitor presence collection ID not configured, skipping tracking"
      );
      return null;
    }

    // Try to update existing presence, if not found, create new
    try {
      const existingPresence = await databases.listDocuments(
        appwriteConfig.databaseId,
        appwriteConfig.visitorPresenceCollectionId, // ✅ Use config
        [Query.equal("visitorId", visitorData.visitorId)]
      );

      if (existingPresence.documents.length > 0) {
        return await databases.updateDocument(
          appwriteConfig.databaseId,
          appwriteConfig.visitorPresenceCollectionId, // ✅ Use config
          existingPresence.documents[0].$id,
          {
            lastSeen: presenceData.lastSeen,
            pageUrl: presenceData.pageUrl,
            isActive: presenceData.isActive,
            widgetOpened: presenceData.widgetOpened,
          }
        );
      }
    } catch (error) {}

    return await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.visitorPresenceCollectionId, // ✅ Use config
      ID.unique(),
      presenceData
    );
  } catch (error) {
    console.error("Error tracking visitor presence:", error);
    // ✅ Don't throw error to prevent widget failure
    return null;
  }
};

// Mark visitor as inactive
export const markVisitorInactive = async (visitorId) => {
  try {
    const existingPresence = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.visitorPresenceCollectionId,
      [Query.equal("visitorId", visitorId)]
    );

    if (existingPresence.documents.length > 0) {
      return await databases.updateDocument(
        appwriteConfig.databaseId,
        appwriteConfig.visitorPresenceCollectionId,
        existingPresence.documents[0].$id,
        {
          isActive: false,
          lastSeen: new Date().toISOString(),
        }
      );
    }
  } catch (error) {
    console.error("Error marking visitor inactive:", error);
  }
};

// Get active visitors for current company
export const getActiveVisitors = async () => {
  try {
    if (!currentCompanyId) {
      console.warn("No company ID set for getting active visitors");
      return [];
    }

    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();

    const response = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.visitorPresenceCollectionId,
      [
        Query.equal("companyId", currentCompanyId),
        Query.equal("isActive", true),
        Query.greaterThan("lastSeen", fiveMinutesAgo),
      ]
    );

    return response.documents;
  } catch (error) {
    console.error("Error getting active visitors:", error);
    return [];
  }
};

// Add this to your appwrite.js file

// 🆕 NEW: Fetch announcements for a company
export async function fetchAnnouncements(companyId) {
  if (!companyId) {
    console.warn("No company ID provided for announcements");
    return {
      enabled: false,
      announcements: [],
    };
  }

  try {
    // Fetch messager style to get announcements
    const messagerStyle = await fetchMessagerStyle(companyId);

    if (!messagerStyle) {
      return {
        enabled: false,
        announcements: [],
      };
    }

    const announcementsEnabled =
      messagerStyle.announcementsEnabled !== undefined
        ? messagerStyle.announcementsEnabled
        : false;
    let announcements = [];

    // Parse announcements data
    if (messagerStyle.announcements) {
      if (Array.isArray(messagerStyle.announcements)) {
        // Check if array contains strings (JSON) or objects
        if (
          messagerStyle.announcements.length > 0 &&
          typeof messagerStyle.announcements[0] === "string"
        ) {
          // Parse JSON strings back to objects
          announcements = messagerStyle.announcements
            .map((announcementStr) => {
              try {
                return JSON.parse(announcementStr);
              } catch (error) {
                console.error("Error parsing announcement JSON:", error);
                return null;
              }
            })
            .filter(Boolean);
        } else {
          // Already objects, use directly
          announcements = messagerStyle.announcements;
        }
      } else if (typeof messagerStyle.announcements === "string") {
        try {
          const parsed = JSON.parse(messagerStyle.announcements);
          announcements = Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          console.error("Error parsing announcements string:", error);
          announcements = [];
        }
      }
    }

    return {
      enabled: announcementsEnabled,
      announcements: announcements,
    };
  } catch (error) {
    console.error("Error fetching announcements:", error);
    return {
      enabled: false,
      announcements: [],
    };
  }
}

// 🆕 NEW: Track announcement interactions
export async function trackAnnouncementInteraction(
  companyId,
  announcementId,
  interactionType,
  additionalData = {}
) {
  try {
    if (!companyId || !announcementId) {
      console.warn("Missing required data for announcement tracking");
      return;
    }

    const interactionData = {
      companyId: companyId,
      announcementId: announcementId,
      interactionType: interactionType, // 'view', 'click', 'dismiss'
      timestamp: new Date().toISOString(),
      visitorId: additionalData.visitorId || null,
      sessionId: additionalData.sessionId || null,
      ...additionalData,
    };

    // You can extend this to store in a separate analytics collection if needed
    // For now, just log the interaction

    return interactionData;
  } catch (error) {
    console.error("Error tracking announcement interaction:", error);
  }
}

// Add these functions to your appwrite.js file:

// 🆕 NEW: Update background texture setting
export async function updateBackgroundTexture(companyId, textureVariant) {
  if (!companyId) {
    throw new Error("Company ID is required to update background texture");
  }

  // Validate texture variant (allow null)
  const validTextures = ['rainbow', 'black', 'image', 'white', 'washed', 'copy', 'none', null];
  if (!validTextures.includes(textureVariant)) {
    throw new Error(`Invalid texture variant. Must be one of: ${validTextures.filter(t => t !== null).join(', ')} or null`);
  }

  try {
    console.log(`🎨 Updating background texture for company ${companyId} to: ${textureVariant}`);

    // Get the messager style document
    const response = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.messagerstyleId,
      [Query.equal("companyID", companyId)]
    );

    if (response.documents.length === 0) {
      throw new Error(`No messager style found for company ID: ${companyId}`);
    }

    const styleDoc = response.documents[0];

    // Update the background texture
    const updatedDoc = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.messagerstyleId,
      styleDoc.$id,
      {
        backgroundTexture: textureVariant
      }
    );

    console.log('✅ Background texture updated successfully');
    
    // Clear cache to force refresh
    cachedMessagerStyle = null;
    
    return updatedDoc;
  } catch (error) {
    console.error("❌ Error updating background texture:", error);
    throw error;
  }
}

// 🆕 NEW: Get current background texture (no fallback)
export async function getBackgroundTexture(companyId) {
  if (!companyId) {
    return null; // No fallback - empty if not set
  }

  try {
    const messagerStyle = await fetchMessagerStyle(companyId);
    return messagerStyle.backgroundTexture || null; // Return null if not set
  } catch (error) {
    console.error("Error fetching background texture:", error);
    return null; // No fallback - empty on error
  }
}

// 🆕 NEW: Get available texture options
export function getAvailableTextures() {
  return [
    { value: null, label: 'None', description: 'Pure gradient, no texture' },
    { value: 'rainbow', label: 'Rainbow', description: 'Colorful rainbow overlay' },
    { value: 'black', label: 'Black', description: 'Dark elegant texture' },
    { value: 'image', label: 'Image', description: 'Subtle image overlay' },
    { value: 'white', label: 'White', description: 'Clean white texture' },
    { value: 'washed', label: 'Washed', description: 'Soft washed texture' },
    { value: 'copy', label: 'Copy', description: 'Copy texture overlay' }
  ];
}

export { databases, client }; // ✅ Add this line

export default {
  setCompanyId,
  getCurrentUser,
  sendEmail,
  sendMessage,
  createConversation,
  processCallbackRequest,
  sendCallbackEmails,
  trackVisitorPresence,
  markVisitorInactive,
  getActiveVisitors,
  fetchAnnouncements, // 🆕 NEW
  trackAnnouncementInteraction, // 🆕 NEW
};
